import React, { useState } from "react";
import {
  actionNames,
  actionPrefixes,
  objectTypes,
  withTrackEvent,
} from "@newsela/snitch";
import { LANG, LANG_EN } from "@/constants/Language";
import { AttachmentQuestion } from "@/types/models/ContentAttachment.interface";
import { StudentQuiz } from "@/types/models/Quiz.interface";
import { QuizOption } from "@/types/models/ContentAttachment.interface";
import useGetUserExperimentSegment from "@/features/useGetUserExperimentSegment";
import useReadAloudMode from "@/view/ReadAloudControls/useReadAloudMode";
import sortQuestionOptionsByPosition from "../utils/sortQuestionOptionsByPosition";
import { fieldsetCss, listCss } from "./QuizQuestion.style";
import QuizQuestionText from "./extensions/QuizQuestionText";
import MultipleChoice from "./extensions/MultipleChoice";
import InteractiveMultipleChoice from "./extensions/InteractiveMultipleChoice";
import { Letter } from "./constants";

interface QuizQuestionProps {
  answerData: StudentQuiz | null;
  contentId: string;
  currentLevelId: string;
  language: LANG;
  isReviewMode: boolean;
  isPreviewMode: boolean;
  isStudent: boolean;
  question: AttachmentQuestion;
  questionPosition: `${bigint}`;
  selectedAnswers:
    | {}
    | {
        [key: string]: {
          quizQuestions: {
            [key: string]: {
              selectedAnswer: string;
            };
          };
        };
      };
  updateSelectedAnswers: (a: string) => void;
  shouldHideQuizAnswers: boolean;
  showAnswersInPresentMode: boolean;
}

type InteractiveMultipleChoiceProps = React.ComponentPropsWithRef<
  typeof InteractiveMultipleChoice
>;
const InteractiveMultipleChoiceWithTrackEvent =
  withTrackEvent<InteractiveMultipleChoiceProps>(InteractiveMultipleChoice);

const QuizQuestion = ({
  answerData,
  contentId,
  currentLevelId,
  language,
  isReviewMode,
  isPreviewMode,
  isStudent,
  question,
  questionPosition,
  selectedAnswers,
  updateSelectedAnswers,
  shouldHideQuizAnswers,
  showAnswersInPresentMode,
}: QuizQuestionProps) => {
  const [currentReadAloudChoice, setCurrentReadAloudChoice] =
    useState<Letter | null>(null);

  const { isReadAloudModeActive } = useReadAloudMode();

  const [readAloudQuizzesVariation] =
    useGetUserExperimentSegment("read_aloud_quizzes");
  const isEnglishQuiz = language === LANG_EN;
  const isReadAloudButtonActive =
    isReadAloudModeActive &&
    readAloudQuizzesVariation === "on" &&
    isEnglishQuiz;

  if (!question) {
    return null;
  }

  const quizAnswerEventProperties = (questionId: string) => ({
    objectType: objectTypes.quizAnswer,
    actionPrefix: actionPrefixes.initiate,
    actionName: actionNames.update,
    streamProperties: {
      content_type: "Article",
      description: "Select Quiz Answer",
      content_id: currentLevelId,
      object_id: questionId,
    },
  });

  const handleSelect = (optionId: string) => {
    updateSelectedAnswers(optionId);
  };

  const sortedOptions = sortQuestionOptionsByPosition(question.options);

  const renderStaticMultipleChoiceQuestion = () => {
    const relevantAnswerData = (answerData?.answers ?? []).find((answer) => {
      return answer.quiz_question_id === question.id;
    });

    const isCorrectChoice = (option: QuizOption) => {
      if (showAnswersInPresentMode) {
        return option.is_correct;
      } else if (isStudent) {
        return relevantAnswerData?.correct_answer === option.id;
      } else {
        return null;
      }
    };

    return (
      <>
        <QuizQuestionText
          id={question.id}
          isReadAloudButtonActive={isReadAloudButtonActive}
          isStatic
          questionPosition={questionPosition}
          text={question.raw_question}
        />
        <ul role="list" css={listCss}>
          {sortedOptions?.map((option: QuizOption) => {
            return (
              <MultipleChoice
                key={option.id}
                choiceIndex={option.position - 1}
                choiceText={option.raw_text}
                isCorrectChoice={isCorrectChoice(option)}
                isReadAloudButtonActive={isReadAloudButtonActive}
                isStudentsChoice={
                  isPreviewMode ? null : relevantAnswerData?.value === option.id
                }
                currentReadAloudChoice={currentReadAloudChoice}
                setCurrentReadAloudChoice={setCurrentReadAloudChoice}
                shouldHideQuizAnswers={shouldHideQuizAnswers}
              />
            );
          })}
        </ul>
      </>
    );
  };

  const renderInteractiveMultipleChoiceQuestion = () => {
    if (isReviewMode) return null;

    const isSelectedChoice = (optionId: string) => {
      if (!optionId) return false;

      return (
        selectedAnswers[contentId as keyof typeof selectedAnswers]?.[
          "quizQuestions"
        ]?.[question?.id]?.["selectedAnswer"] === optionId
      );
    };

    const fieldsetName = `group-${question.id}`;

    return (
      <fieldset css={fieldsetCss}>
        <QuizQuestionText
          isReadAloudButtonActive={isReadAloudButtonActive}
          isStatic={false}
          id={question.id}
          questionPosition={questionPosition}
          text={question.raw_question}
        />
        <div role="radiogroup" aria-labelledby={question.id}>
          {sortedOptions?.map((option: QuizOption) => {
            const optionId = option.id;
            return (
              <InteractiveMultipleChoiceWithTrackEvent
                key={`${question.id}-${optionId}`}
                choiceIndex={option.position - 1}
                choiceText={option.text}
                groupName={fieldsetName}
                handleSelect={handleSelect}
                isReadAloudButtonActive={isReadAloudButtonActive}
                isSelected={isSelectedChoice(optionId)}
                optionId={optionId}
                choiceType="quiz-answer"
                currentReadAloudChoice={currentReadAloudChoice}
                rawChoiceText={option.raw_text}
                setCurrentReadAloudChoice={setCurrentReadAloudChoice}
                {...quizAnswerEventProperties(question.id)}
              />
            );
          })}
        </div>
      </fieldset>
    );
  };

  const renderQuestions = () => {
    if (isPreviewMode || isReviewMode) {
      return renderStaticMultipleChoiceQuestion();
    } else if (!isReviewMode) {
      return renderInteractiveMultipleChoiceQuestion();
    } else {
      return null;
    }
  };

  return <>{renderQuestions()}</>;
};

export default QuizQuestion;
